// help-panel-dialog.component.ts

import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { Subject, firstValueFrom, filter, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';

import { KnowledgeService } from 'src/app/admin-view/components/knowledge-base/services/knowledge.service';
import { KnowledgeCategory, KnowledgeSection, KnowledgeArticle, KnowledgeBlock } from 'src/app/admin-view/components/knowledge-base/interfaces/knowledge.interface';
import { environment } from 'src/environments/environment';
import { FeatureSuggestionDialogComponent } from './feature-suggestion-dialog.component';

type ViewMode = 'home' | 'section' | 'article';

@Component({
  selector: 'app-help-panel-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatDialogModule,
  ],
  // Update the template - move feature-suggestion-cta outside dialog-content as a footer
template: `
<div class="help-dialog-container">
  <!-- Header -->
  <div class="dialog-header">
    <div class="header-left">
      <button mat-icon-button *ngIf="viewMode !== 'home'" (click)="goBack()">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <h3>
        <ng-container *ngIf="viewMode === 'home'">Help Center</ng-container>
        <ng-container *ngIf="viewMode === 'section'">{{ selectedSection?.name }}</ng-container>
        <ng-container *ngIf="viewMode === 'article'">{{ selectedArticle?.title }}</ng-container>
      </h3>
    </div>
    <button mat-icon-button (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Search (only on home) -->
  <div class="dialog-search" *ngIf="viewMode === 'home'">
    <mat-form-field appearance="outline" class="full-width">
      <mat-icon matPrefix>search</mat-icon>
      <input matInput 
             [(ngModel)]="searchQuery" 
             (ngModelChange)="onSearchChange()"
             placeholder="Search help articles...">
      <button mat-icon-button matSuffix *ngIf="searchQuery" (click)="clearSearch()">
        <mat-icon>close</mat-icon>
      </button>
    </mat-form-field>
  </div>

  <!-- Current Page Indicator -->
  <div class="current-page-indicator" *ngIf="viewMode === 'home'">
    <mat-icon>location_on</mat-icon>
    <span>{{ currentPageName }}</span>
  </div>

  <!-- Content -->
  <div class="dialog-content">
    <!-- Loading -->
    <div class="loading" *ngIf="loading">
      <mat-spinner diameter="32"></mat-spinner>
    </div>

    <!-- Search Results -->
    <div class="search-results" *ngIf="!loading && searchQuery && viewMode === 'home'">
      <div class="results-header">
        <span *ngIf="!isSearching">{{ searchResults.length }} result{{ searchResults.length !== 1 ? 's' : '' }}</span>
        <mat-spinner diameter="16" *ngIf="isSearching"></mat-spinner>
      </div>
      <div class="article-item" *ngFor="let article of searchResults" (click)="viewArticle(article)">
        <mat-icon>description</mat-icon>
        <div class="item-content">
          <h4>{{ article.title }}</h4>
          <p>{{ article.section?.category?.name }} → {{ article.section?.name }}</p>
        </div>
        <mat-icon class="arrow">chevron_right</mat-icon>
      </div>
      <div class="empty-results" *ngIf="searchResults.length === 0 && !isSearching">
        <mat-icon>search_off</mat-icon>
        <p>No articles found</p>
      </div>
    </div>

    <!-- Home View (Categories) -->
    <div class="home-view" *ngIf="!loading && !searchQuery && viewMode === 'home'">
      <!-- Matched suggestion -->
      <div class="context-suggestion" *ngIf="matchedSection">
        <p class="suggestion-label">
          <mat-icon>auto_awesome</mat-icon>
          Suggested for this page
        </p>
        <div class="suggestion-item" (click)="viewSection(matchedSection)">
          <mat-icon>{{ matchedSection.icon || 'article' }}</mat-icon>
          <span>{{ matchedSection.name }}</span>
          <mat-icon class="arrow">chevron_right</mat-icon>
        </div>
      </div>

      <!-- All Categories -->
      <div class="categories-list">
        <h4>Browse by Topic</h4>
        <div class="category-group" *ngFor="let category of categories">
          <div class="category-header">
            <mat-icon>{{ category.icon || 'folder' }}</mat-icon>
            <span>{{ category.name }}</span>
          </div>
          <div class="section-item" *ngFor="let section of category.sections" (click)="viewSection(section)">
            <mat-icon>{{ section.icon || 'article' }}</mat-icon>
            <span>{{ section.name }}</span>
            <span class="count">{{ section.articles?.length || 0 }}</span>
            <mat-icon class="arrow">chevron_right</mat-icon>
          </div>
        </div>

        <!-- Empty state -->
        <div class="empty-state" *ngIf="categories.length === 0">
          <mat-icon>menu_book</mat-icon>
          <p>No help articles available yet</p>
        </div>
      </div>
    </div>

    <!-- Section View (Articles) -->
    <div class="section-view" *ngIf="!loading && viewMode === 'section' && selectedSection">
      <p class="section-description" *ngIf="selectedSection.description">
        {{ selectedSection.description }}
      </p>
      <div class="articles-list">
        <div class="article-item" *ngFor="let article of selectedSection.articles" (click)="viewArticle(article)">
          <mat-icon>description</mat-icon>
          <div class="item-content">
            <h4>{{ article.title }}</h4>
            <p *ngIf="article.description">{{ article.description }}</p>
          </div>
          <mat-icon class="arrow">chevron_right</mat-icon>
        </div>
        <div class="empty-state" *ngIf="!selectedSection.articles?.length">
          <mat-icon>article</mat-icon>
          <p>No articles in this section yet</p>
        </div>
      </div>
    </div>

    <!-- Article View (Content) -->
    <div class="article-view" *ngIf="!loading && viewMode === 'article' && selectedArticle">
      <p class="article-description" *ngIf="selectedArticle.description">
        {{ selectedArticle.description }}
      </p>

      <div class="blocks-list">
        <ng-container *ngFor="let block of selectedArticle.blocks">
          <!-- Text Block -->
          <div class="block text-block" *ngIf="block.type === 'text'" [innerHTML]="block.content.html"></div>

          <!-- Image Block -->
          <div class="block image-block" *ngIf="block.type === 'image'">
            <div class="image-thumbnail" (click)="openImageViewer(block)">
              <img [src]="getThumbnailUrl(block)" [alt]="block.content.caption || 'Image'">
              <div class="image-overlay">
                <mat-icon>zoom_in</mat-icon>
              </div>
            </div>
            <p class="caption" *ngIf="block.content.caption">{{ block.content.caption }}</p>
          </div>

          <!-- Video Block -->
          <div class="block video-block" *ngIf="block.type === 'video'">
            <video [src]="getMediaUrl(block)" controls></video>
            <p class="caption" *ngIf="block.content.caption">{{ block.content.caption }}</p>
          </div>

          <!-- Callout Block -->
          <div class="block callout-block" *ngIf="block.type === 'callout'" [attr.data-type]="block.content.calloutType">
            <mat-icon>{{ getCalloutIcon(block.content.calloutType) }}</mat-icon>
            <span>{{ block.content.text }}</span>
          </div>

          <!-- Code Block -->
          <div class="block code-block" *ngIf="block.type === 'code'">
            <div class="code-lang">{{ block.content.language }}</div>
            <pre><code>{{ block.content.code }}</code></pre>
          </div>

          <!-- Divider Block -->
          <div class="block divider-block" *ngIf="block.type === 'divider'">
            <hr>
          </div>
        </ng-container>

        <!-- Empty state -->
        <div class="empty-state" *ngIf="!selectedArticle.blocks?.length">
          <mat-icon>article</mat-icon>
          <p>This article has no content yet</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Feature Suggestion CTA - FIXED AT BOTTOM -->
  <div class="feature-suggestion-footer">
    <div class="cta-divider">
      <span>Have feedback?</span>
    </div>
    <div class="cta-card" (click)="openSuggestionDialog()">
      <div class="cta-icon">
        <mat-icon>lightbulb</mat-icon>
      </div>
      <div class="cta-content">
        <h4>Suggest a Feature</h4>
        <p>Help us improve by sharing your ideas</p>
      </div>
      <mat-icon class="arrow-icon">chevron_right</mat-icon>
    </div>
  </div>
</div>

<!-- Image Lightbox -->
<div class="image-lightbox" *ngIf="lightboxImage" (click)="closeLightbox()">
  <div class="lightbox-content" (click)="$event.stopPropagation()">
    <button mat-icon-button class="lightbox-close" (click)="closeLightbox()">
      <mat-icon>close</mat-icon>
    </button>
    <img [src]="lightboxImage.url" [alt]="lightboxImage.caption || 'Image'">
    <p class="lightbox-caption" *ngIf="lightboxImage.caption">{{ lightboxImage.caption }}</p>
  </div>
</div>
`,
  styles: [`
    .help-dialog-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      max-height: 80vh;
      background: var(--help-bg-primary, #ffffff);
    }

    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid var(--help-border-color, rgba(0, 0, 0, 0.08));
      background: var(--help-bg-secondary, #f8fafc);
      flex-shrink: 0;

      .header-left {
        display: flex;
        align-items: center;
        gap: 8px;

        h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--help-text-primary, #0f172a);
        }

        button {
          color: var(--help-text-secondary, #64748b);
        }
      }

      > button {
        color: var(--help-text-secondary, #64748b);
      }
    }

    .dialog-search {
      padding: 16px 20px;
      border-bottom: 1px solid var(--help-border-color, rgba(0, 0, 0, 0.08));
      flex-shrink: 0;

      .full-width {
        width: 100%;
      }

      ::ng-deep .mat-mdc-form-field-subscript-wrapper {
        display: none;
      }

      ::ng-deep .mat-mdc-text-field-wrapper {
        background: var(--help-input-bg, #f1f5f9);
      }

      ::ng-deep .mat-mdc-form-field-flex {
        background: var(--help-input-bg, #f1f5f9);
      }

      ::ng-deep input {
        color: var(--help-text-primary, #0f172a);
      }

      ::ng-deep .mat-icon {
        color: var(--help-text-muted, #94a3b8);
      }
    }

    .current-page-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;
      background: var(--help-bg-tertiary, #f1f5f9);
      font-size: 12px;
      color: var(--help-text-secondary, #64748b);
      flex-shrink: 0;

      mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        color: var(--help-accent-color, #00a1ff);
      }
    }

    .dialog-content {
      flex: 1;
      overflow-y: auto;
      padding: 20px;
      background: var(--help-bg-primary, #ffffff);
    }

    .loading {
      display: flex;
      justify-content: center;
      padding: 48px;
    }

    // Search Results
    .search-results {
      .results-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
        font-size: 13px;
        color: var(--help-text-secondary, #64748b);
      }

      .empty-results {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 32px;
        color: var(--help-text-muted, #94a3b8);

        mat-icon {
          font-size: 32px;
          width: 32px;
          height: 32px;
          margin-bottom: 8px;
        }
      }
    }

    // Home View
    .home-view {
      .context-suggestion {
        margin-bottom: 24px;
        padding: 16px;
        background: var(--help-suggestion-bg, linear-gradient(135deg, rgba(0, 161, 255, 0.08) 0%, rgba(0, 161, 255, 0.02) 100%));
        border-radius: 12px;
        border: 1px solid var(--help-suggestion-border, rgba(0, 161, 255, 0.2));

        .suggestion-label {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 12px 0;
          font-size: 12px;
          color: var(--help-accent-color, #00a1ff);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;

          mat-icon {
            font-size: 16px;
            width: 16px;
            height: 16px;
          }
        }

        .suggestion-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          background: var(--help-card-bg, #ffffff);
          border-radius: 10px;
          cursor: pointer;
          color: var(--help-text-primary, #0f172a);
          font-weight: 500;
          transition: all 0.2s ease;
          box-shadow: var(--help-card-shadow, 0 2px 4px rgba(0, 0, 0, 0.05));

          &:hover {
            background: var(--help-hover-bg, #f8fafc);
            transform: translateX(4px);
          }

          mat-icon {
            color: var(--help-accent-color, #00a1ff);
          }

          .arrow {
            margin-left: auto;
            color: var(--help-arrow-color, #cbd5e1);
          }
        }
      }

      .categories-list {
        h4 {
          margin: 0 0 16px 0;
          font-size: 13px;
          font-weight: 600;
          color: var(--help-text-secondary, #64748b);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .category-group {
        margin-bottom: 20px;

        .category-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 0;
          font-size: 15px;
          font-weight: 600;
          color: var(--help-text-primary, #0f172a);

          mat-icon {
            font-size: 20px;
            width: 20px;
            height: 20px;
            color: var(--help-accent-color, #00a1ff);
          }
        }
      }

      .section-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        margin-left: 30px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;
        color: var(--help-text-body, #374151);
        transition: all 0.2s ease;

        &:hover {
          background: var(--help-hover-bg, #f8fafc);
        }

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
          color: var(--help-text-muted, #94a3b8);
        }

        .count {
          margin-left: auto;
          font-size: 12px;
          color: var(--help-text-muted, #94a3b8);
          background: var(--help-bg-tertiary, #f1f5f9);
          padding: 2px 8px;
          border-radius: 10px;
        }

        .arrow {
          color: var(--help-arrow-color, #cbd5e1);
        }
      }
    }

    // Section & Article Views
    .section-view, .article-view {
      .section-description, .article-description {
        margin: 0 0 20px 0;
        padding: 16px;
        background: var(--help-bg-secondary, #f8fafc);
        border-radius: 8px;
        color: var(--help-text-secondary, #64748b);
        font-size: 14px;
        line-height: 1.6;
      }
    }

    .article-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      border-radius: 10px;
      cursor: pointer;
      margin-bottom: 8px;
      border: 1px solid var(--help-border-color, rgba(0, 0, 0, 0.06));
      background: var(--help-bg-primary, #ffffff);
      transition: all 0.2s ease;

      &:hover {
        background: var(--help-hover-bg, #f8fafc);
        border-color: var(--help-accent-color, #00a1ff);
      }

      > mat-icon {
        color: var(--help-accent-color, #00a1ff);
        margin-top: 2px;
      }

      .item-content {
        flex: 1;

        h4 {
          margin: 0 0 4px 0;
          font-size: 15px;
          font-weight: 500;
          color: var(--help-text-primary, #0f172a);
        }

        p {
          margin: 0;
          font-size: 13px;
          color: var(--help-text-secondary, #64748b);
          line-height: 1.5;
        }
      }

      .arrow {
        color: var(--help-arrow-color, #cbd5e1);
        margin-top: 2px;
      }
    }

    // Article Content Blocks
    .blocks-list {
      display: flex;
      flex-direction: column;
      gap: 20px;

      .block {
        &.text-block {
          font-size: 14px;
          line-height: 1.8;
          color: var(--help-text-body, #374151);

          ::ng-deep {
            p { margin: 0 0 12px 0; }
            ul, ol { margin: 0 0 12px 0; padding-left: 20px; }
            strong { font-weight: 600; }
            a { color: var(--help-accent-color, #00a1ff); text-decoration: none; &:hover { text-decoration: underline; } }
            h1, h2, h3 { margin: 0 0 12px 0; color: var(--help-text-primary, #0f172a); }
          }
        }

        &.image-block {
          .image-thumbnail {
            position: relative;
            max-width: 200px;
            border-radius: 8px;
            overflow: hidden;
            cursor: pointer;
            border: 1px solid var(--help-border-color, rgba(0, 0, 0, 0.1));
            transition: all 0.2s ease;

            &:hover {
              border-color: var(--help-accent-color, #00a1ff);
              box-shadow: 0 4px 12px var(--help-accent-shadow, rgba(0, 161, 255, 0.2));

              .image-overlay {
                opacity: 1;
              }
            }

            img {
              width: 100%;
              height: auto;
              display: block;
            }

            .image-overlay {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.4);
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0;
              transition: opacity 0.2s ease;

              mat-icon {
                color: #ffffff;
                font-size: 32px;
                width: 32px;
                height: 32px;
              }
            }
          }

          .caption {
            margin: 8px 0 0 0;
            font-size: 12px;
            color: var(--help-text-secondary, #64748b);
            font-style: italic;
          }
        }

        &.video-block {
          video {
            width: 100%;
            max-width: 100%;
            border-radius: 8px;
            box-shadow: var(--help-card-shadow, 0 2px 8px rgba(0, 0, 0, 0.1));
          }

          .caption {
            margin: 10px 0 0 0;
            font-size: 13px;
            color: var(--help-text-secondary, #64748b);
            font-style: italic;
            text-align: center;
          }
        }

        &.callout-block {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          border-radius: 10px;
          font-size: 14px;
          line-height: 1.6;

          mat-icon { flex-shrink: 0; margin-top: 2px; }

          &[data-type="info"] { background: var(--help-callout-info-bg, rgba(59, 130, 246, 0.1)); color: var(--help-callout-info-color, #1e40af); border-left: 4px solid #3b82f6; }
          &[data-type="warning"] { background: var(--help-callout-warning-bg, rgba(234, 179, 8, 0.1)); color: var(--help-callout-warning-color, #92400e); border-left: 4px solid #eab308; }
          &[data-type="tip"] { background: var(--help-callout-tip-bg, rgba(34, 197, 94, 0.1)); color: var(--help-callout-tip-color, #166534); border-left: 4px solid #22c55e; }
          &[data-type="error"] { background: var(--help-callout-error-bg, rgba(239, 68, 68, 0.1)); color: var(--help-callout-error-color, #991b1b); border-left: 4px solid #ef4444; }
        }

        &.code-block {
          .code-lang {
            font-size: 11px;
            text-transform: uppercase;
            color: var(--help-text-muted, #94a3b8);
            margin-bottom: 8px;
            font-weight: 600;
          }

          pre {
            margin: 0;
            padding: 16px;
            background: var(--help-code-bg, #1e293b);
            border-radius: 8px;
            overflow-x: auto;

            code {
              font-family: 'Fira Code', 'Consolas', monospace;
              font-size: 13px;
              color: var(--help-code-color, #e2e8f0);
              line-height: 1.6;
            }
          }
        }

        &.divider-block hr {
          border: none;
          border-top: 1px solid var(--help-border-color, #e2e8f0);
          margin: 8px 0;
        }
      }
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 48px 24px;
      color: var(--help-text-muted, #94a3b8);
      text-align: center;

      mat-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        margin-bottom: 16px;
        color: var(--help-empty-icon-color, #cbd5e1);
      }

      p { margin: 0; font-size: 14px; }
    }

    // Feature Suggestion Footer - FIXED AT BOTTOM
    .feature-suggestion-footer {
      flex-shrink: 0;
      padding: 16px 20px;
      border-top: 1px solid var(--help-border-color, rgba(0, 0, 0, 0.08));
      background: var(--help-bg-secondary, #f8fafc);

      .cta-divider {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        &::before,
        &::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--help-border-color, rgba(0, 0, 0, 0.08));
        }

        span {
          padding: 0 12px;
          font-size: 11px;
          color: var(--help-text-muted, #94a3b8);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .cta-card {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        background: var(--help-cta-bg, linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(139, 92, 246, 0.02) 100%));
        border: 1px solid var(--help-cta-border, rgba(139, 92, 246, 0.2));
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background: var(--help-cta-hover-bg, linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(139, 92, 246, 0.04) 100%));
          border-color: var(--help-cta-hover-border, rgba(139, 92, 246, 0.3));
          transform: translateY(-1px);
          box-shadow: var(--help-cta-shadow, 0 4px 12px rgba(139, 92, 246, 0.15));
        }

        .cta-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;

          mat-icon {
            color: white;
            font-size: 20px;
            width: 20px;
            height: 20px;
          }
        }

        .cta-content {
          flex: 1;

          h4 {
            margin: 0 0 2px 0;
            font-size: 14px;
            font-weight: 600;
            color: var(--help-text-primary, #0f172a);
          }

          p {
            margin: 0;
            font-size: 12px;
            color: var(--help-text-secondary, #64748b);
          }
        }

        .arrow-icon {
          color: var(--help-cta-arrow, #8b5cf6);
          transition: transform 0.2s ease;
        }

        &:hover .arrow-icon {
          transform: translateX(4px);
        }
      }
    }

    // Image Lightbox
    .image-lightbox {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.9);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
      cursor: pointer;

      .lightbox-content {
        position: relative;
        max-width: 90vw;
        max-height: 90vh;
        cursor: default;

        .lightbox-close {
          position: absolute;
          top: -40px;
          right: -40px;
          color: #ffffff;

          mat-icon {
            font-size: 28px;
            width: 28px;
            height: 28px;
          }
        }

        img {
          max-width: 100%;
          max-height: 80vh;
          border-radius: 8px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .lightbox-caption {
          margin: 16px 0 0 0;
          text-align: center;
          color: #ffffff;
          font-size: 14px;
        }
      }
    }

    // =======================================================================
    // DARK THEME - Lighter backgrounds (not too black)
    // =======================================================================
    :host-context(body.dark-theme) {
      .help-dialog-container {
        background: #2a2a2a; // Lighter than #1a1a1a
      }

      .dialog-header {
        border-color: rgba(255, 255, 255, 0.1);
        background: #333333;

        .header-left {
          h3 { color: #f5f5f5; }
          button { color: #b0b0b0; }
        }

        > button { color: #b0b0b0; }
      }

      .dialog-search {
        border-color: rgba(255, 255, 255, 0.1);

        ::ng-deep .mat-mdc-text-field-wrapper,
        ::ng-deep .mat-mdc-form-field-flex {
          background: #383838;
        }

        ::ng-deep input {
          color: #f5f5f5;
        }

        ::ng-deep .mat-icon {
          color: #888888;
        }
      }

      .current-page-indicator {
        background: #333333;
        color: #b0b0b0;
      }

      .dialog-content {
        background: #2a2a2a;
      }

      // Search Results
      .search-results {
        .results-header { color: #b0b0b0; }
        .empty-results { color: #888888; }
      }

      // Home View
      .home-view {
        .context-suggestion {
          background: linear-gradient(135deg, rgba(0, 161, 255, 0.12) 0%, rgba(0, 161, 255, 0.04) 100%);
          border-color: rgba(0, 161, 255, 0.25);

          .suggestion-item {
            background: #383838;
            color: #f5f5f5;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

            &:hover { background: #404040; }

            .arrow { color: #707070; }
          }
        }

        .categories-list h4 { color: #b0b0b0; }

        .category-group .category-header { color: #f5f5f5; }

        .section-item {
          color: #e5e5e5;

          &:hover { background: #383838; }

          mat-icon { color: #888888; }
          .count { background: #404040; color: #b0b0b0; }
          .arrow { color: #707070; }
        }
      }

      // Section & Article Views
      .section-view, .article-view {
        .section-description, .article-description {
          background: #333333;
          color: #b0b0b0;
        }
      }

      .article-item {
        border-color: rgba(255, 255, 255, 0.08);
        background: #2a2a2a;

        &:hover {
          background: #383838;
          border-color: #00a1ff;
        }

        .item-content {
          h4 { color: #f5f5f5; }
          p { color: #b0b0b0; }
        }

        .arrow { color: #707070; }
      }

      // Article Content Blocks
      .blocks-list .block {
        &.text-block {
          color: #e5e5e5;

          ::ng-deep {
            h1, h2, h3 { color: #f5f5f5; }
            a { color: #00a1ff; }
          }
        }

        &.image-block {
          .image-thumbnail {
            border-color: rgba(255, 255, 255, 0.1);

            &:hover {
              border-color: #00a1ff;
              box-shadow: 0 4px 12px rgba(0, 161, 255, 0.25);
            }
          }

          .caption { color: #b0b0b0; }
        }

        &.video-block .caption { color: #b0b0b0; }

        &.callout-block {
          &[data-type="info"] { background: rgba(59, 130, 246, 0.12); color: #93c5fd; }
          &[data-type="warning"] { background: rgba(234, 179, 8, 0.12); color: #fcd34d; }
          &[data-type="tip"] { background: rgba(34, 197, 94, 0.12); color: #86efac; }
          &[data-type="error"] { background: rgba(239, 68, 68, 0.12); color: #fca5a5; }
        }

        &.code-block {
          .code-lang { color: #888888; }
          pre { background: #1a1a1a; }
        }

        &.divider-block hr { border-color: #4a4a4a; }
      }

      .empty-state {
        color: #888888;

        mat-icon { color: #5a5a5a; }
      }

      // Feature Suggestion Footer
      .feature-suggestion-footer {
        border-top-color: rgba(255, 255, 255, 0.1);
        background: #333333;

        .cta-divider {
          &::before,
          &::after { background: rgba(255, 255, 255, 0.1); }
          span { color: #808080; }
        }

        .cta-card {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(139, 92, 246, 0.04) 100%);
          border-color: rgba(139, 92, 246, 0.25);

          &:hover {
            background: linear-gradient(135deg, rgba(139, 92, 246, 0.18) 0%, rgba(139, 92, 246, 0.08) 100%);
            box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
          }

          .cta-content {
            h4 { color: #f5f5f5; }
            p { color: #b0b0b0; }
          }

          .arrow-icon {
            color: #c4b5fd;
          }

          &:hover .arrow-icon {
            color: #e9d5ff;
          }
        }
      }
    }
  `]
})
export class HelpPanelDialogComponent implements OnInit, OnDestroy {
  loading = true;
  viewMode: ViewMode = 'home';
  currentPageName = '';

  // Data
  categories: KnowledgeCategory[] = [];
  matchedCategory: KnowledgeCategory | null = null;
  matchedSection: KnowledgeSection | null = null;
  selectedSection: KnowledgeSection | null = null;
  selectedArticle: KnowledgeArticle | null = null;

  // Search
  searchQuery = '';
  searchResults: KnowledgeArticle[] = [];
  isSearching = false;
  private searchSubject = new Subject<string>();

  // Lightbox
  lightboxImage: { url: string; caption?: string } | null = null;

  // Misc
  publicUrl = environment.publicCloudFlareUrl;
  folder = 'knowledge-base';
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private dialog: MatDialog,  // <-- Add this
    private knowledgeService: KnowledgeService,
    private dialogRef: MatDialogRef<HelpPanelDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { currentRoute?: string },
  ) {}

  ngOnInit() {
    // Initial load
    this.loadContentForRoute();

    // Listen to route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe((event: NavigationEnd) => {
      // Reset to home and reload when route changes
      this.goHome();
      this.loadContentForRoute();
    });

    // Setup search debounce
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(query => {
      this.performSearch(query);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close() {
    this.dialogRef.close();
  }

  async loadContentForRoute() {
    this.loading = true;
    const route = this.router.url.split('?')[0];
    
    // Extract page name from route
    this.currentPageName = this.getPageNameFromRoute(route);
    
    try {
      const result = await firstValueFrom(this.knowledgeService.getByRoute(route));
      this.categories = result.categories || [];
      this.matchedCategory = result.matchedCategory;
      this.matchedSection = result.matchedSection;
    } catch (error) {
      console.error('Error loading knowledge base:', error);
      this.categories = [];
    }

    this.loading = false;
  }

  private getPageNameFromRoute(route: string): string {
    const routeMap: { [key: string]: string } = {
      '/leads': 'Leads',
      '/leads/leads-managment': 'Leads Management',
      '/activities': 'Activities',
      '/reports': 'Reports',
      '/reports/general': 'General Reports',
      '/admissions/dashboard': 'Dashboard',
      '/admissions/engagment-hub': 'Engagement Hub',
      '/admissions/campus': 'Campus',
      '/admissions/programs': 'Programs',
      '/admissions/pipelines': 'Pipelines',
      '/admissions/documents': 'Documents',
      '/admissions/applications': 'Applications',
      '/admissions/labels': 'Labels',
      '/admissions/campaigns': 'Campaigns',
      '/admissions/templates': 'Templates',
      '/admissions/layouts': 'Layouts',
      '/admissions/automations': 'Automations',
      '/admissions/users': 'Users',
      '/settings/profile': 'Profile',
      '/settings/profile/company': 'Company Settings',
    };

    // Find best match
    let bestMatch = '';
    let bestMatchLength = 0;

    for (const [path, name] of Object.entries(routeMap)) {
      if (route.startsWith(path) && path.length > bestMatchLength) {
        bestMatch = name;
        bestMatchLength = path.length;
      }
    }

    return bestMatch || 'Current Page';
  }

  // Navigation
  goHome() {
    this.viewMode = 'home';
    this.selectedSection = null;
    this.selectedArticle = null;
    this.searchQuery = '';
    this.searchResults = [];
  }

  goBack() {
    if (this.viewMode === 'article') {
      this.viewMode = 'section';
      this.selectedArticle = null;
    } else if (this.viewMode === 'section') {
      this.viewMode = 'home';
      this.selectedSection = null;
    }
  }

  viewSection(section: KnowledgeSection) {
    this.selectedSection = section;
    this.viewMode = 'section';
    this.selectedArticle = null;
  }

  async viewArticle(article: KnowledgeArticle) {
    this.loading = true;
    try {
      this.selectedArticle = await firstValueFrom(this.knowledgeService.getArticle(article.id));
      this.viewMode = 'article';
    } catch (error) {
      console.error('Error loading article:', error);
    }
    this.loading = false;
  }

  // Search
  onSearchChange() {
    this.searchSubject.next(this.searchQuery);
  }

  async performSearch(query: string) {
    if (!query.trim()) {
      this.searchResults = [];
      return;
    }

    this.isSearching = true;
    try {
      this.searchResults = await firstValueFrom(this.knowledgeService.searchArticles(query));
    } catch (error) {
      console.error('Error searching:', error);
      this.searchResults = [];
    }
    this.isSearching = false;
  }

  clearSearch() {
    this.searchQuery = '';
    this.searchResults = [];
  }

  // Image Lightbox
  openImageViewer(block: KnowledgeBlock) {
    this.lightboxImage = {
      url: this.getMediaUrl(block),
      caption: block.content.caption,
    };
  }

  closeLightbox() {
    this.lightboxImage = null;
  }

  // Helpers
  getMediaUrl(block: KnowledgeBlock): string {
    if (block.content.key) {
      return `${this.publicUrl}/${this.folder}/${block.content.key}`;
    }
    return '';
  }

  getThumbnailUrl(block: KnowledgeBlock): string {
    if (block.content.key) {
      return `${this.publicUrl}/${this.folder}/thumb_${block.content.key}`;
    }
    return '';
  }

  getCalloutIcon(type: string | undefined): string {
    const icons: { [key: string]: string } = {
      info: 'info',
      warning: 'warning',
      tip: 'lightbulb',
      error: 'error',
    };
    return icons[type || 'info'] || 'info';
  }

  openSuggestionDialog() {
    this.dialog.open(FeatureSuggestionDialogComponent, {
      panelClass: 'suggestion-dialog-panel',
      autoFocus: false,
    });
  }
  
}